/* eslint-disable @typescript-eslint/no-shadow */
import {
  GridOptions,
  GridReadyEvent,
  ModelUpdatedEvent,
  RowDataChangedEvent,
} from '@ag-grid-community/core'
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  heliosDefaultToolPanels,
} from '@helios/ag-grid-config'
import { SavedView } from '@helios/state-management'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
  WorkspaceUpdateEventObject,
} from '@helios/user-preferences'
import { AgGridApi, NfColumnTypes } from '@northfork/ag-grid'
import { AgGridState } from '@northfork/ag-grid-angular'
import { composeFieldPath } from '@northfork/basics'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { PositionGridKey } from '../../../panel-key'
import { Position } from '../../models/position.model'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { createPanelSettingsToolPanel } from 'libs/generic-components/src/lib/panel-settings-tool-panel/utils/panel-settings-tool-panel-sidebar-utils'

const positionsPath = composeFieldPath([] as Position[])

@Component({
  selector: 'hs-positions-grid[data]',
  templateUrl: './positions-grid.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PositionsGridComponent implements OnInit, OnDestroy {
  @Input() data: Position[] | null = null
  @Input() savedViews: SavedView[] | null | undefined = null

  private readonly destroySubject$ = new Subject<void>()
  clearFiltersOnAccountLoadToggle = new FormControl(true)

  /** @internal */
  gridApi?: AgGridApi

  gridOptions: GridOptions = {
    enableBrowserTooltips: true,
    onRowDataChanged: event => this.onRowDataChanged(event),
    columnDefs: [
      {
        field: positionsPath('id'),
        headerName: 'Ext Position ID',
        type: NfColumnTypes.Identifier,
      },
      { field: positionsPath('cusip') },
      { field: positionsPath('isin') },
      { field: positionsPath('sedol') },
      { field: positionsPath('alternateId'), headerName: 'Alternate ID' },
      { field: positionsPath('securityID'), type: NfColumnTypes.Identifier },
      { field: positionsPath('description') },
      { field: positionsPath('units'), type: NfColumnTypes.Identifier },
      { field: positionsPath('marketValue'), type: NfColumnTypes.Identifier },
      {
        field: positionsPath('aggregateInformation'),
        tooltipValueGetter: params => {
          return params.value?.replace(/,"/g, '\n').replace(/"/g, '') ?? ''
        },
      },
      {
        field: positionsPath('alternateIdDesc'),
        headerName: 'Alternate ID Description',
      },
      { field: positionsPath('couponRate'), type: NfColumnTypes.Identifier },
      {
        field: positionsPath('currencyID'),
        type: NfColumnTypes.Identifier,
      },
      {
        field: positionsPath('dataSourceId'),
        headerName: 'Data Source ID',
        type: NfColumnTypes.Identifier,
      },
      { field: positionsPath('factor'), type: NfColumnTypes.Identifier },
      { field: positionsPath('maturityDate') },
      {
        field: positionsPath('originId'),
        headerName: 'Origin ID',
        type: NfColumnTypes.Identifier,
      },
      { field: positionsPath('price'), type: NfColumnTypes.Identifier },
      {
        field: positionsPath('unfactorizedUnits'),
        type: NfColumnTypes.Identifier,
      },
    ],
    icons: {
      panelSettings: generateMdiIcon('mdi-cog'),
    },
    sideBar: generateHeliosAgGridSidebarFromScratch([
      createPanelSettingsToolPanel({
        controls: [
          {
            label: 'Clear Filters on Load',
            formControl: this.clearFiltersOnAccountLoadToggle,
          },
        ],
      }),
      ...heliosDefaultToolPanels,
    ]),
    onGridReady: event => this.onGridReady(event),
    onModelUpdated: event => this.onModelUpdated(event),
  }

  private onModelUpdated(event: ModelUpdatedEvent): void {
    if (event.newData && !event.api.isColumnFilterPresent()) {
      // get state from store
      const filterState = this.savedViews?.find(
        view => view.gridKey === PositionGridKey,
      )
      if (filterState) {
        // Apply preserved filters once grid is rendered
        event.api.setFilterModel(filterState.savedView.filterState)
      }
    }
  }

  private async onGridReady(event: GridReadyEvent) {
    this.gridApi = event
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )
    enableHeliosCopyBehavior(event.api)
    /* why move this later ? so that all filter change events are called before workspace is rendered and only if workspace 
    has filter model it ever again calls filter model change not otherwise avoiding unnecessary race conditions */
    const agGridStateSaved = await this.savedViewService.manageSavedView(
      event,
      PositionGridKey,
      this.destroySubject$,
      true,
      this.getCurrentPanelSettingsObject(),
    )

    this.applyGridConfig(agGridStateSaved)
  }

  constructor(
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
  ) {}

  ngOnInit(): void {
    this.clearFiltersOnAccountLoadToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
    })
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
  }

  private onRowDataChanged(event: RowDataChangedEvent) {
    if (this.data != null) {
      return
    }
    event.api.showLoadingOverlay()
  }

  updateGridConfig() {
    const panelSettingsChangedEvent = new CustomEvent('panelSettingsChanged', {
      detail: {
        api: this.gridApi,
        panelSettings: this.getCurrentPanelSettingsObject(),
      } as WorkspaceUpdateEventObject,
    })
    this.gridApi?.api.dispatchEvent(panelSettingsChangedEvent)
  }

  getCurrentPanelSettingsObject() {
    const panelSettings: { [key: string]: unknown } = {}
    panelSettings['clearFiltersOnAccountLoad'] =
      this.clearFiltersOnAccountLoadToggle.value
    return panelSettings
  }

  private applyGridConfig(agGridStateSaved: AgGridState) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelSettings = agGridStateSaved.panelSettings as any

    if (panelSettings) {
      if ('clearFiltersOnAccountLoad' in panelSettings) {
        this.clearFiltersOnAccountLoadToggle.patchValue(
          Boolean(panelSettings.clearFiltersOnAccountLoad),
          { emitEvent: false },
        )
      }
    }
  }
}
